import { ArchiveBoxIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';

import { useStateContext } from '../../contexts/ContextProvider';

import Button from '../components/Button';
import IconButton from '../components/IconButton';
import PastilleNonLus from '../components/PastilleNonLus';
import Text from '../components/Text';
import { useDeconnexion } from '../api/useDeconnexion';
import { useNombreConversationsNonLues } from '../api/useNombreConversationsNonLues';
import { useEcrans } from '../config/useEcrans';
import { CLE_MESSAGERIE } from '../config/mobileScreens';
import { useFermetureParRetour } from '../hooks/useFermetureParRetour';
import { useFocusSurcouche } from '../hooks/useFocusSurcouche';
import { langueMobile, NOMS_LANGUES, t } from '../i18n/langue';

import SelecteurEtablissement from './SelecteurEtablissement';

/**
 * Le panneau de navigation — décision #17 §3, redessiné.
 *
 * **Plein écran, ouvert depuis un bouton d'en-tête.** Ni barre d'onglets, ni
 * tiroir latéral : dans une PWA installée sur iPhone il n'y a ni barre d'URL ni
 * bouton natif, le balayage depuis le bord *est* le retour arrière, et un tiroir
 * s'ouvrant au bord s'y dispute directement.
 *
 * ## Ce que la première version ratait
 *
 * Elle lisait « entrées ancrées vers le bas » au pied de la lettre et ne mettait
 * **rien** au-dessus : 555 px de vide sur 844, puis quatre lignes de texte
 * empilées — deux de navigation, deux de compte — que rien ne distinguait.
 *
 * ## Ce que ce panneau dit, dans l'ordre
 *
 * **1. Sur quel établissement tu es.** C'est le titre, en haut, et c'est
 * délibéré : #25 a établi que l'erreur chère est de répondre au client du mauvais
 * hôtel, et ce panneau est le seul endroit qui nomme l'établissement quand on est
 * au fond d'une conversation. Le *nom* est proéminent ; la *bascule* ne l'est pas
 * — c'est elle que #25 voulait discrète, pas l'identité.
 *
 * **2. Qui tu es**, en petit, dans le même bloc — établissement et compte
 * répondent tous deux à « où suis-je », et les séparer créait deux îlots avec un
 * vide au milieu.
 *
 * **3. Où tu peux aller** — seul bloc ancré en bas, sous le pouce, comme #17 le
 * demande. Les entrées sont les seules cibles pleine largeur du panneau : ce sont
 * les seules qu'on vise vraiment, et elles sont dimensionnées pour être visées à
 * bout de bras plutôt qu'au millimètre.
 *
 * L'écran actif porte une **barre pleine en couleur de marque sur son bord
 * d'attaque**, et non un cadre. C'est le même vocabulaire que les onglets de canal
 * du composeur et que les pastilles de filtre — « actif = un trait de marque » est
 * devenu la langue de l'app, plutôt qu'une décoration de plus.
 *
 * « Se déconnecter » reste **au-dessus** de la navigation : le bas de l'écran est
 * la zone la plus facile à atteindre, et ce n'est pas là qu'on met la sortie.
 */
interface PanneauNavigationProps {
    onClose: () => void;
}

export default function PanneauNavigation({ onClose }: PanneauNavigationProps) {
    const { eligibles } = useEcrans();
    const { user, currentFacility } = useStateContext();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Le compteur du badge de la liste principale — 0 quand la messagerie n'est
    // pas éligible, le hook porte déjà son propre gating (#17 §5).
    const nombreNonLues = useNombreConversationsNonLues();

    /**
     * La déconnexion coupe l'abonnement — elle ne le faisait jamais, sur le web
     * déjà (#78) — et révoque désormais la session serveur. Une seule fois pour
     * toute l'app : voir `useDeconnexion`.
     */
    const { seDeconnecter, enCours: deconnexionEnCours } = useDeconnexion();

    // Le panneau n'est monté que lorsqu'il est ouvert (jamais de `ouverte: false`
    // à lui passer) : le focus part donc systématiquement à son montage.
    const ref = useFocusSurcouche(true);
    // Le balayage de retour referme le panneau au lieu de quitter l'écran (D6).
    useFermetureParRetour(true, onClose);

    const estGroupe = currentFacility?.is_group === true;

    /**
     * Les destinations SECONDAIRES — des sous-vues, pas des écrans.
     *
     * L'accès aux conversations fermées vit ici et pas en quatrième onglet (#30) :
     * les trois onglets de #16 trient la boîte de réception — geste permanent —
     * quand les fermées sont une destination qu'on visite exprès.
     *
     * Mais il n'entre PAS au registre : il y compterait dans les seuils 0/1/≥2 de
     * #17, et un compte n'ayant que la messagerie basculerait de « un seul écran »
     * à « deux » alors que rien n'a changé pour lui.
     */
    const secondaires =
        eligibles.some((e) => e.cle === CLE_MESSAGERIE)
            ? [{ cle: 'fermees', chemin: '/messaging/closed', libelle: t('nav.conversationsFermees'), icone: ArchiveBoxIcon }]
            : [];

    const aller = (chemin: string) => {
        navigate(chemin);
        onClose();
    };

    return (
        <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-label={t('coque.navigation')}
            tabIndex={-1}
            className="tf-shell fixed inset-0 z-40 flex flex-col bg-main-bg-color"
        >
            <div className="flex shrink-0 justify-end px-3 py-2">
                <IconButton libelle={t('coque.fermerNavigation')} onPress={onClose}>
                    <XMarkIcon className="h-6 w-6 text-text-color" />
                </IconButton>
            </div>

            {/* ── 1. L'établissement ─────────────────────────────────────────── */}
            <div className="shrink-0 px-5">
                <Text variante="petit" ton="attenue" className="uppercase tracking-wide">
                    {t('coque.etablissement')}
                </Text>
                <Text variante="titre" as="h2" className="mt-1 text-main-color-dark">
                    {currentFacility?.name ?? '—'}
                </Text>
                {estGroupe && (
                    <Text variante="petit" ton="attenue" className="mt-0.5">
                        {t('coque.tousLesEtablissements')}
                    </Text>
                )}

                {/* Sans proéminence (#25) : un lien discret, pas un bouton plein. */}
                <SelecteurEtablissement onBascule={onClose} />

                {/* ── 2. Le compte, dans le même bloc ────────────────────────── */}
                <div className="mt-6 border-t border-border-color-light pt-4">
                    <Text variante="petit" ton="attenue">
                        {user?.name || user?.email || ''}
                    </Text>
                    {/*
                      * L'état des notifications vit dans le bloc COMPTE (#21), pas
                      * dans la navigation : ce n'est pas une destination de travail,
                      * c'est le lieu où l'on va quand on croit ne rien recevoir.
                      */}
                    <Button
                        libelle={t('coque.etatNotifications')}
                        variante="lien"
                        alignement="debut"
                        espacement="aucun"
                        onPress={() => aller('/notifications')}
                    />
                    <Button
                        libelle={`${t('langue.titre')} · ${NOMS_LANGUES[langueMobile()]}`}
                        variante="lien"
                        alignement="debut"
                        espacement="aucun"
                        onPress={() => aller('/langue')}
                    />
                    <Button
                        libelle={deconnexionEnCours ? t('coque.deconnexionEnCours') : t('coque.seDeconnecter')}
                        variante="lien"
                        alignement="debut"
                        espacement="aucun"
                        disabled={deconnexionEnCours}
                        onPress={() => void seDeconnecter()}
                    />
                </div>
            </div>

            {/* ── 3. La navigation, sous le pouce ────────────────────────────── */}
            {(eligibles.length > 1 || secondaires.length > 0) && (
                <nav className="mt-auto shrink-0 border-t border-border-color-light py-2">
                    {secondaires.length > 0 && (
                        <ul className="border-b border-border-color-light pb-2">
                            {secondaires.map((entree) => {
                                const Icone = entree.icone;
                                const actif = pathname === entree.chemin;

                                return (
                                    <li key={entree.cle}>
                                        {/* mobile-check-ignore — même ligne que la navigation
                                            principale, en second groupe. */}
                                        <button
                                            type="button"
                                            aria-current={actif ? 'page' : undefined}
                                            onClick={() => aller(entree.chemin)}
                                            className={`flex w-full items-center gap-3 border-l-4 py-4 pl-4 pr-5 text-left ${
                                                actif
                                                    ? 'border-main-color bg-main-color-light'
                                                    : 'border-transparent'
                                            }`}
                                        >
                                            <Icone
                                                className={`h-6 w-6 shrink-0 ${
                                                    actif ? 'text-main-color' : 'text-text-color-light'
                                                }`}
                                                aria-hidden="true"
                                            />
                                            <Text
                                                as="span"
                                                variante="petit"
                                                ton={actif ? 'marque' : 'attenue'}
                                            >
                                                {entree.libelle}
                                            </Text>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {/*
                     * À un seul écran, pas de liste principale (#17 §4) : elle
                     * n'aurait qu'une ligne, celle où l'on est déjà. Le groupe
                     * secondaire, lui, mène ailleurs — il reste.
                     */}
                    <ul>
                        {(eligibles.length > 1 ? eligibles : []).map((ecran) => {
                            const Icone = ecran.icone;
                            const actif =
                                pathname === ecran.chemin || pathname.startsWith(`${ecran.chemin}/`);

                            return (
                                <li key={ecran.cle}>
                                    {/* mobile-check-ignore — ligne de navigation : le Button
                                        du socle ne sait pas porter la barre d'état actif. */}
                                    <button
                                        type="button"
                                        aria-current={actif ? 'page' : undefined}
                                        onClick={() => aller(ecran.chemin)}
                                        className={`flex w-full items-center gap-3 border-l-4 py-4 pl-4 pr-5 text-left ${
                                            actif
                                                ? 'border-main-color bg-main-color-light'
                                                : 'border-transparent'
                                        }`}
                                    >
                                        <Icone
                                            className={`h-6 w-6 shrink-0 ${
                                                actif ? 'text-main-color' : 'text-text-color-light'
                                            }`}
                                            aria-hidden="true"
                                        />
                                        <Text
                                            as="span"
                                            variante="sousTitre"
                                            className={actif ? 'text-main-color' : ''}
                                        >
                                            {ecran.libelle}
                                        </Text>
                                        {ecran.cle === CLE_MESSAGERIE && (
                                            <PastilleNonLus nombre={nombreNonLues} />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            )}
        </div>
    );
}
